import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Check, Star, Quote } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ReelVideo from "../components/ReelVideo";
import LearnMore from "../components/LearnMore";
import { GoogleBadge, REAL_REVIEW } from "../components/Testimonials";
import useSEO from "../hooks/useSEO";

const SERVICES = [
  {
    id: "implantologia",
    eyebrow: "Il cuore dello studio",
    title: "Implantologia avanzata",
    desc: "Risolviamo anche i casi complessi: implantologia zigomatica e iuxtaossea (subperiostea) per pazienti con poco osso, All-on-Four e All-on-Six per l'arcata completa.",
    points: [
      "Carico immediato: denti fissi in 6 ore, se le condizioni lo consentono",
      "Possibilità di richiedere, con un piccolo sovraprezzo, la protesi su impianti definitiva in ceramica nano ibrida",
      "Impianti italiani certificati e tracciabili",
      "TAC 3D e pianificazione digitale in sede",
      "Interventi seguiti dal Dott. Gattuso, 25 anni di esperienza",
    ],
    img: "/carico-immediato-flyer.jpg",
    imgSquare: true,
    reel: "/videos/reel6_dc.mp4",
    reelPoster: "/videos/reel6_poster.jpg",
    reelCaption: "Un impianto resterà nella tua bocca per anni.",
  },
  {
    id: "protesi",
    eyebrow: "Estetica e funzione",
    title: "Faccette estetiche",
    desc: "Faccette estetiche drill-free in ceramica, realizzate nel nostro laboratorio con materiali certificati: un sorriso nuovo senza trapano.",
    points: [
      "€ 550 a faccetta, provvisorio incluso",
      "Faccette estetiche drill-free",
      "Laboratorio con materiali certificati",
    ],
    video: "/videos/faccette-patrizia.mp4",
    videoPoster: "/videos/faccette-patrizia-poster.jpg",
    videoCaption: "La dottoressa Patricia sta prendendo le impronte con lo scanner intraorale per realizzare 16 faccette estetiche ❤",
  },
  {
    id: "ortodonzia",
    eyebrow: "Allineare, a ogni età",
    title: "Ortodonzia invisibile e tradizionale",
    desc: "Mascherine trasparenti, discrete e rimovibili: gli allineatori Invisalign® raddrizzano i denti senza farlo notare. Siamo certificati Invisalign® e applichiamo un nuovo metodo che velocizza il trattamento. Brackets tradizionali e apparecchi per bambini quando servono.",
    points: [
      "Mascherine trasparenti, discrete e rimovibili",
      "Studio certificato Invisalign®",
      "Nuovo metodo che velocizza il trattamento",
      "Odontoiatria pediatrica su misura",
    ],
    img: "/ortodonzia-adulto.jpg",
    img2: "/ortodonzia-team.jpg",
    showPucinka: true,
  },
  {
    id: "parodontologia",
    eyebrow: "Gengive sane",
    title: "Parodontologia e igiene",
    desc: "La parodontite è la principale causa di perdita dei denti negli adulti: la trattiamo con protocolli conservativi e manutenzione professionale regolare.",
    points: [
      "Trattamento della parodontite",
      "Igiene professionale domiciliare dello studio",
      "Istruzioni personalizzate di igiene orale",
      "Richiami periodici programmati",
    ],
    img: null,
  },
];

const PROCESS = [
  { n: "01", t: "Prima visita e TAC", d: "Incontro, esame clinico e TAC dental scan in sede: vediamo tutto subito, senza attese." },
  { n: "02", t: "Piano e preventivo", d: "Ti presentiamo il piano di cura con tempi e costi chiari, prima di iniziare." },
  { n: "03", t: "Trattamento", d: "Interventi programmati con tecnologie certificate, dal carico immediato all'ortodonzia." },
  { n: "04", t: "Controlli e manutenzione", d: "Follow-up e igiene periodica per proteggere il risultato nel tempo." },
];

const FAQ = [
  {
    q: "Quanto costa un impianto dentale?",
    a: "L'implantologia a carico immediato costa € 5.900; l'overdenture su 2 impianti parte da € 3.900. Ogni caso è diverso: dopo la prima visita con TAC ricevi un preventivo personalizzato e definitivo, senza sorprese.",
  },
  {
    q: "Cos'è il carico immediato?",
    a: "Quando le condizioni cliniche lo permettono, fissiamo i denti sulle nuove radici implanto-supportate già in 6 ore dall'intervento, dopo una visita specialistica e diagnosi radiologiche: eviti mesi con i denti mancanti.",
  },
  {
    q: "Non ho abbastanza osso: posso comunque fare gli impianti?",
    a: "Nella maggior parte dei casi sì. Lo studio esegue implantologia zigomatica e iuxtaossea (subperiostea), tecniche avanzate pensate proprio per gli ossi deficitari.",
  },
  {
    q: "L'Invisalign® è adatto a me?",
    a: "Allinea i denti con mascherine trasparenti e rimovibili, praticamente invisibili. Durante la prima visita valutiamo il tuo caso e ti mostriamo il percorso previsto.",
  },
  {
    q: "Dove si trovano e come arrivo?",
    a: "Siamo in Via Gerbido 15/3, a Chivasso: a 700 metri dalla Stazione FS e con parcheggio comodo per i pazienti.",
  },
];

const goToSection = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -120, duration: 1.1 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  window.history.replaceState(null, "", `/servizi#${id}`);
};

export default function Servizi() {
  useSEO({
    title: "Servizi | Dentalchivasso — Implantologia Avanzata, Protesi, Invisalign a Chivasso",
    description: "Implantologia avanzata con carico immediato (denti fissi in 6 ore), All-on-Four e All-on-Six, protesi e faccette, ortodonzia invisibile Invisalign, parodontologia e igiene. TAC 3D in sede a Chivasso.",
    path: "/servizi",
  });
  return (
    <main>
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-40 pb-16" data-testid="servizi-hero">
        <Eyebrow>Servizi</Eyebrow>
        <MaskedLines
          className="mt-6 font-display font-medium tracking-tight text-ink text-5xl sm:text-6xl leading-[1.04]"
          delay={0.2}
          lines={[
            "Odontoiatria completa,",
            <span key="2" className="italic text-teal">dalla TAC alla manutenzione.</span>,
          ]}
        />
        <p className="mt-6 max-w-2xl text-mutedc leading-relaxed">
          Uno studio, tutti i trattamenti: implantologia avanzata, protesi, ortodonzia,
          parodontologia, igiene e odontoiatria pediatrica — con diagnostica diretta in sede.
        </p>
      </section>

      {/* ---- MENU RAPIDO SPECIALITÀ ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16" data-testid="servizi-menu-rapido">
        <Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={goToSection(s.id)}
                className="group rounded-3xl border border-line bg-paper p-6 flex flex-col hover:border-teal/40 hover:shadow-lift transition-all duration-300"
                data-testid={`menu-rapido-${s.id}`}
              >
                <span className="font-display text-2xl font-semibold text-teal">0{i + 1}</span>
                <span className="mt-2 font-semibold text-sm text-ink">{s.title}</span>
                <span className="mt-1 text-xs text-mutedc leading-relaxed">{s.eyebrow}</span>
                <ArrowRight size={15} className="mt-4 text-teal group-hover:translate-x-1 transition-transform" />
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 space-y-24 md:space-y-32" data-testid="servizi-detail-list">
        {SERVICES.map((s, i) => (
          <div key={s.id} id={s.id} className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`servizio-block-${s.id}`}>
            <Reveal className={s.reel ? "" : "order-none"}>
              {(s.reel || s.img2) ? (
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                  <div className="col-span-full sm:col-span-3 relative">
                    <img src={s.img} alt={s.title} className={`relative rounded-[2rem] w-full border border-line shadow-soft ${s.imgSquare ? "aspect-square object-cover" : "h-full min-h-[300px] object-cover"}`} />
                  </div>
                  <div className="col-span-full sm:col-span-2">
                    {s.reel ? (
                      <ReelVideo src={s.reel} poster={s.reelPoster} caption={s.reelCaption} testid={`servizio-reel-${s.id}`} />
                    ) : (
                      <img src={s.img2} alt="Il team di ortodonzia di Dentalchivasso con una giovane paziente" className="relative rounded-[2rem] w-full border border-line shadow-soft" />
                    )}
                    {s.showPucinka && (
                      <div className="mt-4 rounded-2xl border border-line bg-paper p-5" data-testid="pucinka-card">
                        <p className="text-sm font-bold text-ink">Dr.ssa Olesja Pucinska</p>
                        <p className="text-xs text-teal font-semibold mt-0.5">Odontoiatra ortodontista</p>
                        <p className="mt-2 text-xs text-mutedc leading-relaxed">
                          Master in ortodonzia tradizionale · Master in ortodonzia invisibile con
                          allineatori · Certificata Invisalign<span className="align-super text-[0.55em]">®</span> ·
                          Certificata allineatori N2 (metodo veloce)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : s.video ? (
                <ReelVideo
                  src={s.video}
                  poster={s.videoPoster}
                  caption={s.videoCaption}
                  aspect="aspect-[16/9]"
                  testid={`servizio-video-${s.id}`}
                />
              ) : s.img ? (
                <div className="relative">
                  <div className={`absolute -top-4 w-full h-full rounded-[2rem] border border-gold/40 ${i % 2 === 1 ? "-right-4" : "-left-4"}`} />
                  <img src={s.img} alt={s.title} className={`relative rounded-[2rem] w-full border border-line shadow-soft ${s.imgSquare ? "aspect-square object-cover" : "aspect-[4/3] object-cover"}`} />
                </div>
              ) : (
                <div className="relative rounded-[2rem] overflow-hidden border border-line shadow-soft aspect-[4/3]" data-testid={`servizio-visual-${s.id}`}>
                  <img
                    src="/lavoro-hola.jpg"
                    alt="Dr. Kola e Silvia al lavoro: igiene e prevenzione"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <BadgeCheck size={26} className="text-teal-mist" strokeWidth={1.5} />
                    <p className="mt-2 font-display text-2xl md:text-3xl text-ivory font-medium leading-snug">Prevenire costa meno che curare.</p>
                    <p className="mt-1 text-xs md:text-sm text-ivory/75">Il richiamo periodico protegge denti e impianti nel tempo.</p>
                  </div>
                </div>
              )}
            </Reveal>
            <Reveal delay={0.1}>
              <Eyebrow>{s.eyebrow}</Eyebrow>
              <h2 className="mt-4 font-display font-medium tracking-tight text-ink text-3xl md:text-[2.6rem] leading-[1.1]">{s.title}</h2>
              <p className="mt-5 text-mutedc leading-relaxed">{s.desc}</p>
              <ul className="mt-6 space-y-3">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={16} className="text-teal mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                data-testid={`servizio-cta-${s.id}`}
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-teal-deep group"
              >
                Prenota una consulenza
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        ))}
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pb-16 text-center">
        <LearnMore to="/studio" testid="learn-more-servizi-studio" />
      </div>

      {/* ---- PROCESSO ---- */}
      <section className="bg-sand/70 border-y border-line" data-testid="servizi-processo-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24">
          <Reveal>
            <Eyebrow>Come lavoriamo</Eyebrow>
            <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08] max-w-2xl">
              Il tuo percorso, in <span className="italic text-teal">quattro passi chiari.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="h-full">
                <div className="rounded-3xl border border-line bg-paper p-8 h-full" data-testid={`processo-step-${p.n}`}>
                  <p className="font-display text-5xl font-semibold text-gold">{p.n}</p>
                  <p className="mt-4 font-semibold text-ink">{p.t}</p>
                  <p className="mt-2 text-sm text-mutedc leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-24" data-testid="servizi-faq-section">
        <Reveal>
          <Eyebrow>Domande frequenti</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
            Tutto quello che ci chiedono <span className="italic text-teal">in studio.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10" data-testid="servizi-faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-line">
                <AccordionTrigger className="text-left font-semibold text-ink hover:text-teal hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-mutedc leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* ---- RECENSIONI ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24" data-testid="servizi-testimonial-section">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 items-center rounded-[2.5rem] border border-line bg-teal-mist/40 p-7 md:p-12">
            <div className="lg:col-span-5">
              <Eyebrow>Recensioni dei pazienti</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                La fiducia di chi <span className="italic text-teal">ci ha scelto.</span>
              </h2>
              <div className="mt-8">
                <GoogleBadge />
              </div>
            </div>
            <div className="lg:col-span-7">
              <figure
                className="rounded-3xl border border-line bg-paper p-8 md:p-10"
                data-testid="servizi-testimonial-card"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-1 text-gold" aria-label="Valutazione 5 stelle su 5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <Quote size={30} className="text-teal/25" strokeWidth={1.5} />
                </div>
                <blockquote className="mt-6 font-display text-lg md:text-xl leading-relaxed text-ink/85 whitespace-pre-line">
                  “{REAL_REVIEW.text}”
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-line flex items-center justify-between flex-wrap gap-2">
                  <p className="font-semibold text-sm text-ink">{REAL_REVIEW.name}</p>
                  <p className="text-xs text-mutedc">{REAL_REVIEW.tag}</p>
                </figcaption>
              </figure>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}